import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { LuHouse, LuBuilding2 } from "react-icons/lu";
import { API, colors, PART_LABELS, PART_FLAGS } from "../shared/constants";
import { styles, inputStyle } from "../shared/styles";
import { FlatAssetRegister } from "./FlatAssetRegister";
import { UnitsRegister } from "./UnitsRegister";
import { buildRegisterTabs } from "../inspections/tabInspections";


function IconBtn({ onClick, title, icon, label, hoverBg, hoverColor }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button onClick={onClick} title={title}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? hoverBg : "#f1f5f9",
        border: "none", borderRadius: "6px", padding: "8px 10px",
        cursor: "pointer", color: hovered ? hoverColor : "#64748b",
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px",
        fontSize: "12px", fontWeight: "600",
        transition: "background 0.15s, color 0.15s",
      }}>
      {icon}
      {label}
    </button>
  );
}

export function AssetRegisterTabs({ siteId, site, onSiteChange }) {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState(null);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [editingDraft, setEditingDraft] = useState("");
  const [showAddCompartment, setShowAddCompartment] = useState(false);
  const [newCompartmentName, setNewCompartmentName] = useState("");
  const [addingCompartment, setAddingCompartment] = useState(false);
  const [addCompartmentError, setAddCompartmentError] = useState("");
  const [showAddTenantCategory, setShowAddTenantCategory] = useState(false);
  const [newTenantCategoryName, setNewTenantCategoryName] = useState("");
  const [addingTenantCategory, setAddingTenantCategory] = useState(false);
  const [addTenantCategoryError, setAddTenantCategoryError] = useState("");
  const editInputRef = useRef(null);
  const nameInputRef = useRef(null);
  const tenantCategoryInputRef = useRef(null);

  const loadAreas = () => {
    axios.get(`${API}/site-areas/?site=${siteId}`)
      .then((res) => { setAreas(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadAreas(); }, [siteId]);

  useEffect(() => {
    if (editingKey) editInputRef.current?.focus();
  }, [editingKey]);

  useEffect(() => {
    if (showAddCompartment) nameInputRef.current?.focus();
  }, [showAddCompartment]);

  useEffect(() => {
    if (showAddTenantCategory) tenantCategoryInputRef.current?.focus();
  }, [showAddTenantCategory]);

  // Shared with the inspection modals, so a per-tab inspection is always
  // scoped to exactly the tab the user sees here.
  const tabs = buildRegisterTabs(site, areas);
  // Disambiguate tabs that happen to share the same name (e.g. leftover
  // duplicates created before section names were required to be unique
  // per site) so they're never visually indistinguishable in the bar.
  const nameCounts = {};
  tabs.forEach((t) => {
    const key = t.label.trim().toLowerCase();
    nameCounts[key] = (nameCounts[key] || 0) + 1;
  });
  tabs.forEach((t) => {
    const key = t.label.trim().toLowerCase();
    if (nameCounts[key] > 1) {
      t.label = `${t.label} (${PART_LABELS[t.areaType] || t.areaType})`;
    }
  });
  const tabsKey = tabs.map((t) => t.key).join(",");

  useEffect(() => {
    if (!tabs.find((t) => t.key === activeKey)) {
      setActiveKey(tabs[0]?.key ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsKey]);

  // A failed delete must say why rather than appearing to do nothing.
  const deleteFailed = (err) =>
    window.alert(
      err.response?.data?.detail ||
        err.response?.data?.[0] ||
        "Could not delete this tab. Please try again."
    );

  const handleDeleteArea = async (area) => {
    if (!window.confirm(`Remove "${area.name}" and all its assets? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/site-areas/${area.id}/`);
    } catch (err) {
      deleteFailed(err);
    }
    loadAreas();
  };

  const startEditing = (tab) => {
    setEditingKey(tab.key);
    setEditingDraft(tab.area.name);
  };

  const saveEditing = async (tab) => {
    const trimmed = editingDraft.trim();
    setEditingKey(null);
    if (!trimmed || trimmed === tab.area.name) return;
    try {
      await axios.patch(`${API}/site-areas/${tab.area.id}/`, { name: trimmed });
      loadAreas();
    } catch (err) {
      window.alert(err.response?.data?.name?.[0] || "Rename failed.");
    }
  };

  const handleAddCompartment = async () => {
    const type = "common";
    const trimmedName = newCompartmentName.trim();
    if (!trimmedName) {
      setAddCompartmentError("Name required.");
      return;
    }
    setAddingCompartment(true);
    setAddCompartmentError("");
    try {
      if (!site[PART_FLAGS[type]]) {
        await axios.patch(`${API}/sites/${siteId}/`, { [PART_FLAGS[type]]: true });
      }
      const res = await axios.post(`${API}/site-areas/`, { site: siteId, area_type: type, name: trimmedName });
      await loadAreas();
      if (onSiteChange) await onSiteChange();
      setActiveKey(String(res.data.id));
      setNewCompartmentName("");
      setShowAddCompartment(false);
    } catch (err) {
      setAddCompartmentError(err.response?.data?.name?.[0] || "Save failed.");
    }
    setAddingCompartment(false);
  };

  const handleAddTenantCategory = async () => {
    const trimmedName = newTenantCategoryName.trim();
    if (!trimmedName) {
      setAddTenantCategoryError("Name required.");
      return;
    }
    setAddingTenantCategory(true);
    setAddTenantCategoryError("");
    try {
      if (!site.has_units) {
        await axios.patch(`${API}/sites/${siteId}/`, { has_units: true });
      }
      const res = await axios.post(`${API}/site-areas/`, {
        site: siteId, area_type: "unit_group", name: trimmedName,
      });
      await loadAreas();
      if (onSiteChange) await onSiteChange();
      setActiveKey(String(res.data.id));
      setNewTenantCategoryName("");
      setShowAddTenantCategory(false);
    } catch (err) {
      setAddTenantCategoryError(err.response?.data?.name?.[0] || "Save failed.");
    }
    setAddingTenantCategory(false);
  };

  if (loading) return <div style={styles.loading}>Loading...</div>;

  const activeTab = tabs.find((t) => t.key === activeKey);

  const chipStyle = (isActive) => ({
    position: "relative",
    width: "160px", flex: "0 0 auto", boxSizing: "border-box",
    display: "flex", alignItems: "center", justifyContent: "center",
    textAlign: "center",
    padding: "10px 20px 10px 10px",
    cursor: "pointer",
    fontSize: "13px", lineHeight: "1.3",
    fontWeight: isActive ? "600" : "400",
    color: isActive ? colors.activeBlue : "#666",
    borderBottom: isActive ? `3px solid ${colors.activeBlue}` : "3px solid transparent",
    transition: "all 0.15s",
  });

  let content = null;
  if (activeTab) {
    if (activeTab.areaType === "unit_group") {
      content = (
        <UnitsRegister
          key={activeTab.key}
          siteId={siteId}
          scopeType="area"
          scopeId={activeTab.area.id}
        />
      );
    } else {
      content = (
        <FlatAssetRegister
          key={activeTab.key}
          siteId={siteId}
          scopeType="area"
          scopeId={activeTab.area.id}
          scopeLabel={activeTab.label}
        />
      );
    }
  }

  return (
    <div>
      <div style={{
        backgroundColor: "#fff", borderBottom: `1px solid ${colors.border}`,
        display: "flex", alignItems: "center", paddingLeft: "8px",
        flexWrap: "wrap", gap: "0", marginBottom: "8px",
      }}>
        {tabs.length === 0 && (
          <div style={{ padding: "10px 16px", fontSize: "13px", color: "#94a3b8" }}>
            No asset register compartments yet.
          </div>
        )}

        {tabs.map((tab) => {
          const isActive = tab.key === activeKey;
          const isEditing = editingKey === tab.key;
          const showDeleteX = hoveredKey === tab.key && !isEditing;
          return (
            <div key={tab.key}
              title={tab.kind === "area" ? tab.area.name : undefined}
              onMouseEnter={() => setHoveredKey(tab.key)}
              onMouseLeave={() => setHoveredKey(null)}
              onClick={() => { if (!isEditing) setActiveKey(tab.key); }}
              style={{ ...chipStyle(isActive), cursor: isEditing ? "text" : "pointer" }}>
              {isEditing ? (
                <input
                  ref={editInputRef}
                  value={editingDraft}
                  onChange={(e) => setEditingDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveEditing(tab);
                    if (e.key === "Escape") setEditingKey(null);
                  }}
                  onBlur={() => saveEditing(tab)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    fontSize: "13px", fontWeight: "600", border: "none",
                    borderBottom: "1px solid #94a3b8", outline: "none",
                    background: "transparent", color: colors.activeBlue,
                    width: "100%", maxWidth: "140px", padding: "0", textAlign: "center",
                  }}
                />
              ) : (
                <span
                  onDoubleClick={(e) => { e.stopPropagation(); startEditing(tab); }}
                  title="Double-click to rename"
                  style={{
                    display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
                    overflow: "hidden", wordBreak: "break-word",
                  }}>
                  {tab.label}
                </span>
              )}

              {showDeleteX && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteArea(tab.area);
                  }}
                  title="Delete this register and everything in it"
                  style={{
                    position: "absolute", top: "4px", right: "4px",
                    background: "none", border: "none", cursor: "pointer",
                    color: "#ccc", fontSize: "14px", padding: "0 2px", lineHeight: "1",
                    display: "inline-flex", alignItems: "center",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = colors.slate}
                  onMouseLeave={(e) => e.currentTarget.style.color = "#ccc"}>
                  ×
                </button>
              )}
            </div>
          );
        })}

        <div style={{ marginLeft: "auto" }}>
        {showAddCompartment ? (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 8px", flexWrap: "wrap" }}>
            <input
              ref={nameInputRef}
              value={newCompartmentName}
              onChange={(e) => setNewCompartmentName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddCompartment();
                if (e.key === "Escape") { setShowAddCompartment(false); setNewCompartmentName(""); setAddCompartmentError(""); }
              }}
              placeholder="Common area name..."
              style={{ ...inputStyle, fontSize: "13px", padding: "5px 10px", width: "200px" }}
            />
            {addCompartmentError && <span style={{ fontSize: "11px", color: colors.red }}>{addCompartmentError}</span>}
            <button onClick={handleAddCompartment} disabled={addingCompartment}
              style={{
                backgroundColor: colors.green, color: "#fff", border: "none",
                borderRadius: "6px", padding: "5px 14px", fontSize: "13px", fontWeight: "600", cursor: "pointer",
              }}>
              Save
            </button>
            <button onClick={() => { setShowAddCompartment(false); setNewCompartmentName(""); setAddCompartmentError(""); }}
              style={{
                background: "none", border: `1px solid ${colors.border}`, borderRadius: "6px",
                padding: "5px 10px", fontSize: "13px", cursor: "pointer", color: "#666",
              }}>
              Cancel
            </button>
          </div>
        ) : showAddTenantCategory ? (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 8px", flexWrap: "wrap" }}>
            <input
              ref={tenantCategoryInputRef}
              value={newTenantCategoryName}
              onChange={(e) => setNewTenantCategoryName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddTenantCategory();
                if (e.key === "Escape") { setShowAddTenantCategory(false); setNewTenantCategoryName(""); setAddTenantCategoryError(""); }
              }}
              placeholder="Tenant category name (e.g. Offices)..."
              style={{ ...inputStyle, fontSize: "13px", padding: "5px 10px", width: "220px" }}
            />
            {addTenantCategoryError && <span style={{ fontSize: "11px", color: colors.red }}>{addTenantCategoryError}</span>}
            <button onClick={handleAddTenantCategory} disabled={addingTenantCategory}
              style={{
                backgroundColor: colors.green, color: "#fff", border: "none",
                borderRadius: "6px", padding: "5px 14px", fontSize: "13px", fontWeight: "600", cursor: "pointer",
              }}>
              Save
            </button>
            <button onClick={() => { setShowAddTenantCategory(false); setNewTenantCategoryName(""); setAddTenantCategoryError(""); }}
              style={{
                background: "none", border: `1px solid ${colors.border}`, borderRadius: "6px",
                padding: "5px 10px", fontSize: "13px", cursor: "pointer", color: "#666",
              }}>
              Cancel
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 8px" }}>
            <IconBtn
              onClick={() => setShowAddCompartment(true)}
              title="Add a Common Area register"
              icon={<LuHouse size={16} />}
              label="+ Common Area"
              hoverBg="#dcfce7" hoverColor={colors.green}
            />
            <IconBtn
              onClick={() => setShowAddTenantCategory(true)}
              title="Add a Tenant register"
              icon={<LuBuilding2 size={16} />}
              label="+ Tenant"
              hoverBg="#ede9fe" hoverColor="#7c3aed"
            />
          </div>
        )}
        </div>
      </div>

      {content}
    </div>
  );
}
